import { BasicEvent } from "@h4x/common";
import type { HewSync } from "./HewSync";

type PermissionsSegment =
	| { type: "Empty" }
	| { type: "Literal"; value: string }
	| { type: "Wildcard" }
	| { type: "Any" };

function permissionsSegmentFrom(s: string): PermissionsSegment {
	if (s === "*") {
		return { type: "Wildcard" };
	} else if (s === "") {
		return { type: "Empty" };
	} else {
		return { type: "Literal", value: s };
	}
}

function permissionsSegmentFromCheck(s: string): PermissionsSegment {
	if (s === "*") {
		return { type: "Wildcard" };
	} else if (s === "?") {
		return { type: "Any" };
	} else if (s === "") {
		return { type: "Empty" };
	} else {
		return { type: "Literal", value: s };
	}
}

function permissionsSegmentToString(segment: PermissionsSegment): string {
	switch (segment.type) {
		case "Empty":
			return "";
		case "Literal":
			return segment.value;
		case "Wildcard":
			return "*";
		case "Any":
			return "?";
	}
}

export class HewSyncPermissions {
	private permissions: Map<string, HewSync.Permission[]> = new Map();
	private initialized = false;
	private debugMode = false;
	private side: "admin" | "user" = undefined!;
	public readonly onUpdate = new BasicEvent<() => void>();
	constructor() {}

	public setSide(side: "admin" | "user") {
		this.side = side;
	}

	public setPermissions(permissions: { [K: string]: HewSync.Permission[] }) {
		this.permissions.clear();
		for (let [key, value] of Object.entries(permissions)) {
			this.permissions.set(key, value);
		}

		this.initialized = true;
		this.onUpdate.execute();
	}

	public updatePermissions(account: string, organization: string, permissions: HewSync.Permission[]) {
		this.permissions.set(organization, permissions);
		this.initialized = true;
		this.onUpdate.execute();
	}

	public setDebugMode(value: boolean) {
		this.debugMode = value;
	}

	public check(action: string, organization: string) {
		console.log("[HewSyncPermissions] check", {
			action,
			organization,
			permissions: this.permissions,
			side: this.side,
			initialized: this.initialized,
			debugMode: this.debugMode
		});
		if (!this.initialized) {
			return true;
		}

		if (this.debugMode && JSON.parse(localStorage.getItem("HEWMEN_PERM_SKIP") || "0") === "1") {
			return true;
		}

		if (action === "view:organization") {
			return true;
		}

		let permissions = this.permissions.get(this.side === "admin" ? "0AdminAdminAdmin" : organization);
		if (permissions === undefined) {
			return false;
		}

		return permissions.some((permission) => HewSyncPermissions.allows(permission, action, [["*"]]));
	}

	private static allows(permission: HewSync.Permission, action: string, resources: string[][]): boolean {
		let matchAction = permission.actions.some((existingAction) =>
			HewSyncPermissions.matchSegment(existingAction, action, ":")
		);

		// each group of resources needs to match at least one of the existing resources in the permission
		let matchResources = resources.every((requiredGroup) => {
			return requiredGroup.some((requiredResource) => {
				return permission.resources.some((existingResource) => {
					return HewSyncPermissions.matchSegment(
						existingResource,
						requiredResource,
						":",
						(existingSegment, requiredSegment) => {
							return HewSyncPermissions.matchSegment(existingSegment, requiredSegment, "/");
						}
					);
				});
			});
		});

		return matchAction && matchResources;
	}

	private static matchSegment(
		existing: string,
		required: string,
		delimiter: string,
		innerCheck: (existing: string, required: string) => boolean = (a, b) => a === b
	): boolean {
		if (existing === required) {
			return true;
		}

		if (([...required].length === 1 || !required.includes(delimiter)) && existing === "*") {
			return true;
		}

		if (required === "?") {
			return true;
		}

		const valueSegments = existing.split(delimiter).map(permissionsSegmentFrom);
		const requiredSegmentsRaw = required.split(delimiter).map(permissionsSegmentFromCheck);
		const lastIndex = requiredSegmentsRaw.length - 1;
		const requiredSegments = requiredSegmentsRaw.map((segment, i) => [i === lastIndex, segment] as const);

		let lastSegmentOfValueIsWildcard = false;
		let isFirstSegment = true;
		let valueIndex = 0;
		let requiredIndex = 0;

		while (true) {
			const value: PermissionsSegment | undefined = valueSegments[valueIndex];
			const requiredEntry: readonly [boolean, PermissionsSegment] | undefined = requiredSegments[requiredIndex];
			valueIndex++;
			requiredIndex++;

			if (value !== undefined && requiredEntry !== undefined) {
				const [isLast, requiredSegment] = requiredEntry;

				if (value.type === "Empty" && requiredSegment.type === "Empty") {
					lastSegmentOfValueIsWildcard = false;
					if (isFirstSegment) {
						isFirstSegment = false;
						continue;
					} else {
						return (
							(existing.endsWith("/") && required.endsWith("/")) || (!existing.endsWith("/") && !required.endsWith("/"))
						);
					}
				}

				if (value.type === "Wildcard") {
					isFirstSegment = false;
					lastSegmentOfValueIsWildcard = true;
					continue;
				}

				if (isLast && requiredSegment.type === "Any") {
					return true;
				}

				isFirstSegment = false;
				lastSegmentOfValueIsWildcard = false;

				if (innerCheck(permissionsSegmentToString(value), permissionsSegmentToString(requiredSegment))) {
					continue;
				} else {
					return false;
				}
			}

			if (value === undefined && requiredEntry !== undefined) {
				return lastSegmentOfValueIsWildcard ? !existing.endsWith("/") : false;
			}

			if (value === undefined && requiredEntry === undefined) {
				if (lastSegmentOfValueIsWildcard && !existing.endsWith("/")) {
					return true;
				}
				return (
					(existing.endsWith("/") && required.endsWith("/")) || (!existing.endsWith("/") && !required.endsWith("/"))
				);
			}

			return false;
		}
	}
}
